import { useCallback, useEffect, useMemo, useState } from "react";
import { BiCollapseVertical, BiExpandVertical } from "react-icons/bi";
import { FaShieldAlt, FaUser } from "react-icons/fa";

import { FaMask } from "react-icons/fa6";

import { useNavigate } from "react-router-dom";
import Button from "../../../components/Button";
import Card from "../../../components/Card";
import SortAndFilterSection, {
  SortAndFilterableItem,
} from "../../../components/SortAndFilterSection";
import { useAdminApi } from "../../../hooks/api-hooks/useAdminApi";
import { useAuth } from "../../../hooks/utility-hoks/useAuth";
import { User } from "../../../types/rokaTypes";
import { selectableFilterOptions, sortOptions } from "./userAdminConfig";
import UserAdminForm from "./UserAdminForm";

const UserAdmin = () => {
  const navigate = useNavigate();
  const { fetchUsers } = useAdminApi();
  const { user: currentUser, isImpersonating } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [filteredAndSortedUsers, setFilteredAndSortedUsers] = useState<User[]>(
    [],
  );

  useEffect(() => {
    fetchUsers()
      .then(setUsers)
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  }, [fetchUsers]);

  const expandUser = (user: User) => {
    setExpandedUser(user.username);
  };

  const collapseUser = () => {
    setExpandedUser(null);
  };

  const createNewUser = () => {
    navigate("/admin/users/new");
  };

  const handleUserChange = (updatedUser: User) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.username === updatedUser.username ? updatedUser : user,
      ),
    );
  };

  const handleSortAndFilterChange = useCallback(
    (items: SortAndFilterableItem[]) => {
      const updatedUsers = items
        .map((item) => users.find((user) => user.username === item.id))
        .filter((user): user is User => user !== undefined);
      setFilteredAndSortedUsers(updatedUsers);
    },
    [users],
  );

  const sortAndFilterItems: SortAndFilterableItem[] = useMemo(
    () =>
      users.map((user) => ({
        id: user.username,
        role: user.isAdmin ? "admin" : "user",
        status: user.isDisabled ? "disabled" : "active",
      })),
    [users],
  );

  return (
    <div className="user-admin-view">
      <div className="filter-sort-controls">
        <div className="new-user">
          <Button
            buttonStyle="flat"
            intent="success"
            onClick={() => createNewUser()}
          >
            Ny bruker
          </Button>
        </div>
        <SortAndFilterSection
          selectableFilterOptions={selectableFilterOptions}
          sortOptions={sortOptions}
          items={sortAndFilterItems}
          onChange={handleSortAndFilterChange}
        />
      </div>

      {filteredAndSortedUsers.map((user) => {
        const isCurrentlyImpersonated =
          isImpersonating && currentUser?.username === user.username;

        return (
          <Card className={`user-card`} key={user.username}>
            <div className="main">
              <span
                className={`user-icon ${user.isDisabled ? "disabled" : ""} ${user.isAdmin ? "admin" : "user"}`}
              >
                {user.isAdmin ? <FaShieldAlt /> : <FaUser />}
              </span>
              {isCurrentlyImpersonated && (
                <span className="user-icon impersonation">
                  <FaMask />
                </span>
              )}
              <span className="username">{user.username}</span>
              <span className="separator">|</span>
              <span className="site">{user.site}</span>

              <div className="actions">
                <Button
                  className="user-actions"
                  buttonStyle="outline"
                  onClick={() =>
                    expandedUser === user.username
                      ? collapseUser()
                      : expandUser(user)
                  }
                >
                  {expandedUser === user.username ? (
                    <BiCollapseVertical />
                  ) : (
                    <BiExpandVertical />
                  )}
                </Button>
              </div>
            </div>
            {expandedUser === user.username && (
              <UserAdminForm user={user} onChange={handleUserChange} />
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default UserAdmin;
